import { Link } from "react-router-dom";
import { Check, Trophy, Flame, UtensilsCrossed, Croissant, Shirt, Mic, Camera, Leaf, Users, Coffee, ArrowRight, Swords } from "lucide-react";
import { TeamMark } from "@/components/Bits";

const MINI = [["Toi", 69, "+3"], ["Alex", 60, "+3"], ["Thomas", 51, ""], ["Julie", 36, "−4"]];

export function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[290px] rotate-[-2deg] rounded-[44px] border border-white/15 bg-[#05070D] p-3 shadow-2xl shadow-black/60" data-testid="landing-phone">
      <div className="mx-auto mb-2 h-5 w-24 rounded-full bg-black" />
      <div className="space-y-3 rounded-[32px] bg-[#0B1020] p-3.5">
        <div className="flex items-center justify-between text-[10px] text-slate-400"><span className="font-bold text-white">Ligue du Bureau</span><span className="rounded-full border border-white/10 px-2 py-0.5">Exemple</span></div>
        <div className="rounded-2xl border border-white/10 bg-[#131B30] p-3" data-testid="landing-preview-card">
          <div className="mb-2 flex items-center justify-between text-[10px]">
            <span className="font-mono-num rounded-full bg-white/5 px-2 py-0.5 text-slate-200">19 h 30</span>
            <span className="font-bold uppercase tracking-widest text-slate-500">Exemple illustratif</span>
          </div>
          <p className="mb-2 text-xs font-bold" data-testid="landing-preview-matchup">Boston Celtics vs New York Knicks</p>
          {[["BOS", "Boston Celtics", true], ["NYK", "New York Knicks", false]].map(([abbr, name, sel]) => (
            <div key={abbr} className={`mb-1.5 flex items-center gap-2 rounded-xl border p-2 ${sel ? "border-[#B7F52A]/60 bg-[#B7F52A]/10" : "border-white/10"}`}>
              <TeamMark team={{ abbr }} size={26} /><span className="flex-1 text-[11px] font-bold">{name}</span>
              {sel && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#B7F52A] text-[#05070D]"><Check size={12} strokeWidth={3} /></span>}
            </div>
          ))}
          <p className="mt-2 flex items-center gap-1.5 text-[10px] font-semibold" data-testid="landing-preview-saved"><span className="h-1.5 w-1.5 rounded-full bg-[#B7F52A] shadow-[0_0_8px_#B7F52A]" /> Prédiction enregistrée</p>
          <p className="mt-0.5 pl-3 text-[10px] text-slate-500">Modifiable jusqu'au début du match</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#131B30] p-3">
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">Classement</p>
          {MINI.map(([n, p, m], i) => (
            <p key={n} className={`flex items-center justify-between py-0.5 text-[11px] ${n === "Toi" ? "font-bold text-[#B7F52A]" : "text-slate-200"}`}>
              <span>{i + 1}. {n} {i === 0 && <Trophy size={10} className="inline" />}{i === MINI.length - 1 && <span className="ml-1 text-[9px] font-bold uppercase text-red-300">dernier</span>}</span>
              <span className="font-mono-num">{p} {m && <span className={m.startsWith("+") ? "text-[#B7F52A]" : "text-red-300"}>{m}</span>}</span>
            </p>
          ))}
        </div>
        <div className="rounded-2xl border border-[#B7F52A]/30 bg-[#B7F52A]/[0.07] p-3">
          <p className="flex items-center gap-1.5 text-[11px] font-bold"><Swords size={12} className="text-[#B7F52A]" /> Défi du mois</p>
          <p className="mt-1 text-[11px] text-slate-300">Le dernier apporte les croissants lundi.</p>
          <p className="mt-1 text-[10px] text-[#B7F52A]">4/4 ont accepté</p>
        </div>
      </div>
      <p className="mt-2 text-center text-[9px] text-slate-500">Données fictives, à titre d'exemple</p>
    </div>
  );
}

const CHALLENGES = [
  [UtensilsCrossed, "Le resto", "Le dernier invite le premier au resto."],
  [Croissant, "Les croissants", "Le dernier apporte les viennoiseries lundi."],
  [Shirt, "Le maillot rival", "Le dernier porte le maillot de l'équipe rivale une journée."],
  [Mic, "Le karaoké", "Le dernier chante la chanson choisie par le groupe."],
  [Camera, "La photo imposée", "Le premier choisit la photo de profil du dernier pendant une semaine."],
  [Leaf, "Sans dépense", "Le dernier choisit la musique de la prochaine soirée."],
];

export function ChallengesStrip() {
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-5 pb-16" data-testid="landing-challenges">
      <p className="mpl-eyebrow text-[#B7F52A]">Ce qui rend le jeu addictif</p>
      <h2 className="mt-2 font-display text-4xl font-extrabold uppercase">Le défi, c'est ta ligue qui le choisit</h2>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Avant la période, la ligue se met d'accord sur un défi. À la fin, le premier savoure, le dernier relève le défi. Quelques idées :</p>
      <div className="mpl-scroll-x -mx-5 mt-6 flex snap-x gap-3 overflow-x-auto px-5 pb-2">
        {CHALLENGES.map(([Icon, t, d], i) => (
          <div key={t} className="mpl-card mpl-rise w-56 shrink-0 snap-start p-5" style={{ animationDelay: `${i * 60}ms` }} data-testid={`landing-challenge-${i + 1}`}>
            <Icon className="text-[#B7F52A]" size={22} />
            <p className="mt-3 text-base font-bold">{t}</p>
            <p className="mt-1 text-sm text-slate-400">{d}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-500" data-testid="landing-challenges-note">Les défis sont toujours volontaires : chacun les accepte ou les refuse, et une option sans dépense est toujours possible. L'app ne gère aucun argent.</p>
    </section>
  );
}

export function Audience() {
  const items = [
    [Users, "Entre amis", "La ligue de la bande : on se chambre dans le fil, on réagit avec des GIFs, et le dernier relève le défi à la prochaine soirée."],
    [Coffee, "Entre collègues", "La ligue du bureau : de quoi animer la pause café. Les ligues sont privées : seuls les membres voient les pronostics et le classement."],
  ];
  return (
    <section className="relative z-10 mx-auto grid max-w-6xl gap-4 px-5 pb-16 md:grid-cols-2" data-testid="landing-audience">
      {items.map(([Icon, t, d]) => (
        <div key={t} className="mpl-card p-6">
          <Icon className="text-[#B7F52A]" size={24} />
          <h3 className="mt-4 font-display text-2xl font-extrabold uppercase">{t}</h3>
          <p className="mt-2 text-sm text-slate-300">{d}</p>
        </div>
      ))}
    </section>
  );
}

export function FinalCta() {
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-5 pb-16" data-testid="landing-final-cta">
      <div className="mpl-card flex flex-col items-start gap-4 border-[#B7F52A]/30 p-8 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase"><Flame className="text-[#B7F52A]" /> Prêt à défier ta bande ?</p>
          <p className="mt-1 text-sm text-slate-400">Tu as reçu une invitation ? Ouvre ton lien et rejoins ta ligue en une minute.</p>
        </div>
        <a href="#invitation" onClick={() => setTimeout(() => document.querySelector('[data-testid="landing-invite-input"]')?.focus({ preventScroll: true }), 400)} className="mpl-btn-primary shrink-0" data-testid="landing-final-cta-button">J'ai une invitation <ArrowRight size={16} /></a>
      </div>
      <p className="mt-3 text-xs text-slate-500">Déjà membre ? <Link to="/connexion" className="text-[#B7F52A] underline-offset-2 hover:underline">Connecte-toi</Link>.</p>
    </section>
  );
}
